import {
  CheckOutlined,
  CloseOutlined,
  InfoCircleFilled,
} from '@ant-design/icons';
import { Form, Switch } from 'antd';
import React from 'react';

import { CustomInput } from 'app/components/fields/Input';
import type { ExtranetPackage } from 'app/typings/packages';
import { assertNever, parseInputNumber } from 'app/utils/typing';

import './RuleSection.scss';
import type { RuleKeys, RuleType } from './Utils';
import { disableableLabel, renderPricingRuleError } from './Utils';

type RulesProps = {
  ruleInfo: RuleType;
  isRuleActive: boolean;
  packages: ExtranetPackage[];
  toggleSwitch: (switchValue: boolean) => void;
  isSingleRate: boolean;
  isRuleInvalid: boolean;
};

export const RuleSection = ({
  ruleInfo,
  isRuleActive,
  packages,
  toggleSwitch,
  isSingleRate,
  isRuleInvalid,
}: RulesProps) => {
  const form = Form.useFormInstance();
  const formValues = Form.useWatch([], form);

  const getRuleForm = (key: RuleKeys) => {
    switch (key) {
      case 'standardDiscount':
        const getFieldName = ruleInfo.fieldName
          ? [...ruleInfo.fieldName, 'discount']
          : 'discount';

        return (
          <Form.Item
            name={getFieldName}
            label={disableableLabel(
              'Staycation price = (BAR + package content) -',
              isRuleActive
            )}
            noStyle={false}
          >
            <CustomInput
              className="discountInput"
              suffix="%"
              onWheel={(e) => e.currentTarget.blur()}
              min={0}
              id="discountInput"
              disabled={!isRuleActive}
              parser={parseInputNumber}
            />
          </Form.Item>
        );

      case 'lastMinuteDiscount':
        const getDaysFieldName = ruleInfo.fieldName
          ? [...ruleInfo.fieldName, 'lastMinuteDays']
          : 'lastMinuteDays';

        const getValueFieldName = ruleInfo.fieldName
          ? [...ruleInfo.fieldName, 'lastMinuteDiscountValue']
          : 'lastMinuteDiscountValue';

        return (
          <>
            <Form.Item
              name={
                ruleInfo.fieldName
                  ? [...ruleInfo.fieldName, 'packageId']
                  : 'packageId'
              }
              hidden
            />
            <div className="section-input">
              <Form.Item
                name={getDaysFieldName}
                label={disableableLabel('From', isRuleActive)}
              >
                <CustomInput
                  className="discountInput"
                  onWheel={(e) => e.currentTarget.blur()}
                  min={0}
                  id="discountInput"
                  disabled={!isRuleActive}
                  parser={parseInputNumber}
                />
              </Form.Item>
              <Form.Item
                label={disableableLabel('days before check-in :', isRuleActive)}
              >
                {' '}
              </Form.Item>
            </div>
            <Form.Item
              name={getValueFieldName}
              label={disableableLabel(
                'Staycation price = (BAR + package content) -',
                isRuleActive
              )}
            >
              <CustomInput
                className="discountInput"
                suffix="%"
                onWheel={(e) => e.currentTarget.blur()}
                min={0}
                id="lastMinuteAutoDiscountRate"
                disabled={!isRuleActive}
                parser={parseInputNumber}
              />
            </Form.Item>
          </>
        );
      default:
        assertNever(key);
    }
  };

  const getFormValue = () => {
    switch (ruleInfo.key) {
      case 'standardDiscount':
        return formValues?.discount;
      case 'lastMinuteDiscount':
        return formValues?.lastMinuteDiscountValue;

      default:
        assertNever(ruleInfo.key);
    }
  };

  return (
    <div
      className={`${
        !!ruleInfo.fieldName ? 'multiPackage_section' : 'section'
      } ${isRuleActive ? '' : 'disabled'}`}
    >
      <div className="section-header">
        <div className="section-title">
          {ruleInfo.icon && <div className="section-icon">{ruleInfo.icon}</div>}
          <p className="title">{ruleInfo.title}</p>
        </div>
        <Form.Item
          name={
            ruleInfo.key === 'standardDiscount'
              ? 'discountIsActivated'
              : 'lastMinuteIsActivated'
          }
          noStyle
        >
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            checked={!!isRuleActive}
            onChange={toggleSwitch}
          />
        </Form.Item>
      </div>
      {getRuleForm(ruleInfo.key)}
      {renderPricingRuleError(
        ruleInfo.key,
        isRuleActive,
        formValues,
        packages,
        isRuleInvalid,
        ruleInfo.pkgId
      )}
      {ruleInfo.information && (
        <p className="explanation_discount">
          Staycation price = (BAR + package content){' '}
          <span
            className={`explanation_discount__percent ${
              isRuleActive ? '' : 'disabled'
            }`}
          >
            - {Math.trunc(getFormValue()) || '0'}%
          </span>
        </p>
      )}
      {!isSingleRate && ruleInfo.information && (
        <div className="explanation_multiRate_information">
          <InfoCircleFilled />
          {ruleInfo.information}
        </div>
      )}
    </div>
  );
};
